import styled from '@emotion/styled';
import { XIcon } from 'components/ui/icons';
import { UploadButton } from 'components/UploadCenter/UploadContin/styles';
import React, { useState } from 'react';
import Dropzone from 'react-dropzone';

interface AttachmentLike {
  actualFileName: string;
  customFileName?: string;
}

interface PropTypes {
  title: string;
  badgeLabel: string;
  helperText: string;
  dropzoneText: string;
  dropzoneActiveText: string;
  files: AttachmentLike[];
  onFilesSelected: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onRenameFile?: (index: number, newName: string) => void;
  disabled?: boolean;
  accentColor?: string;
  badgeBackground?: string;
  beforeUploadContent?: React.ReactNode;
  maxWidth?: number | string;
}

const Card = styled.div<{ accentColor: string }>`
  border: 1px solid #e5e5ea;
  border-left: 4px solid ${props => props.accentColor};
  border-radius: 8px;
  margin-top: 16px;
  padding: 16px;
  box-sizing: border-box;
  width: 100%;
  background: #ffffff;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const CardTitle = styled.div`
  color: #1f2933;
  font-size: 15px;
  font-weight: 700;
`;

const Badge = styled.div<{ badgeBackground: string; accentColor: string }>`
  background: ${props => props.badgeBackground};
  border-radius: 999px;
  color: ${props => props.accentColor};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 4px 10px;
  text-transform: uppercase;
`;

const HelperText = styled.p`
  color: #52606d;
  font-size: 13px;
  line-height: 1.5;
  margin: 8px 0 0;
`;

const UploadRow = styled.div<{ disabled: boolean; dragActive: boolean }>`
  align-items: center;
  border: 1px solid ${props => (props.dragActive ? '#333333' : '#e5e5ea')};
  border-radius: 4px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding: 12px 16px;
  width: 100%;
  background: ${props => (props.disabled ? '#f7f7f9' : '#ffffff')};
`;

const UploadText = styled.span`
  color: #333333;
  font-size: 14px;
  margin-right: 12px;
  overflow-wrap: anywhere;
`;

const ActionButton = styled(UploadButton)`
  margin-right: 0;

  &:disabled {
    background-color: #9b9b9b;
    cursor: default;
  }
`;

const AttachmentTag = styled.div<{ clickable: boolean }>`
  align-items: center;
  background-color: #f7f7f9;
  border-radius: 5px;
  cursor: ${props => (props.clickable ? 'text' : 'default')};
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  min-height: 40px;
  padding: 10px 16px;
`;

const AttachmentName = styled.span`
  color: #333333;
  font-size: 14px;
  margin-right: 12px;
  overflow-wrap: anywhere;
`;

const RemoveButton = styled.button<{ disabled: boolean }>`
  background: transparent;
  border: 0;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  flex-shrink: 0;
  padding: 0;
`;

const RenameInput = styled.input`
  background-color: #fff;
  border: 1px solid #ff867f;
  border-radius: 5px;
  color: #333333;
  font-size: 14px;
  height: 32px;
  width: 100%;

  &:focus {
    border: 1px solid #ff867f;
    outline: none;
  }
`;

const AttachmentUploadCard = (props: PropTypes) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const disabled = props.disabled ?? false;

  const finishRename = (index: number) => {
    if (props.onRenameFile != null && renameValue !== getDisplayName(props.files[index])) {
      props.onRenameFile(index, renameValue);
    }

    setEditingIndex(null);
    setRenameValue('');
  };

  const startRename = (index: number) => {
    if (!props.onRenameFile || disabled) {
      return;
    }

    setEditingIndex(index);
    setRenameValue(getDisplayName(props.files[index]));
  };

  return (
    <Card
      accentColor={props.accentColor ?? '#0f7b6c'}
      style={{ maxWidth: props.maxWidth ?? 480 }}
    >
      <CardHeader>
        <CardTitle>{props.title}</CardTitle>
        <Badge
          badgeBackground={props.badgeBackground ?? '#e6f4f1'}
          accentColor={props.accentColor ?? '#0f7b6c'}
        >
          {props.badgeLabel}
        </Badge>
      </CardHeader>
      {props.helperText ? <HelperText>{props.helperText}</HelperText> : null}
      {props.beforeUploadContent}
      <Dropzone onDrop={props.onFilesSelected} noClick disabled={disabled}>
        {({ getRootProps, getInputProps, isDragActive, open }) => (
          <>
            <div {...getRootProps()}>
              <UploadRow disabled={disabled} dragActive={isDragActive}>
                <input {...getInputProps()} />
                <UploadText>{isDragActive ? props.dropzoneActiveText : props.dropzoneText}</UploadText>
                <ActionButton
                  type="button"
                  disabled={disabled}
                  onClick={event => {
                    event.stopPropagation();
                    if (!disabled) {
                      open();
                    }
                  }}
                >
                  Add Files
                </ActionButton>
              </UploadRow>
            </div>
            {props.files.map((file, index) => {
              const isEditing = editingIndex === index;

              return (
                <AttachmentTag
                  key={`${file.actualFileName}-${index}`}
                  clickable={props.onRenameFile != null}
                  onClick={() => startRename(index)}
                >
                  {isEditing ? (
                    <RenameInput
                      autoFocus
                      value={renameValue}
                      onChange={event => setRenameValue(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          finishRename(index);
                        }
                      }}
                      onBlur={() => finishRename(index)}
                    />
                  ) : (
                    <AttachmentName>{getDisplayName(file)}</AttachmentName>
                  )}
                  <RemoveButton
                    type="button"
                    disabled={disabled}
                    onClick={event => {
                      event.stopPropagation();
                      if (!disabled) {
                        props.onRemoveFile(index);
                      }
                    }}
                  >
                    <XIcon />
                  </RemoveButton>
                </AttachmentTag>
              );
            })}
          </>
        )}
      </Dropzone>
    </Card>
  );
};

const getDisplayName = (file: AttachmentLike) => file.customFileName || file.actualFileName;

export default AttachmentUploadCard;
